import React, { useCallback, useEffect, useState } from "react";
import { type ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { IClub, ITransactionUserSummary } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import { httpService } from "src/services/http.service";
import { Config } from "@eagerdog/config";
import Button from "src/components/Button/Button";
import { download } from "src/components/Download/DownloadManager";

import { ActionsRenderer } from "./ActionsRenderer";
import { TransactionDetailRenderer } from "./TransactionDetailRenderer";

import styles from "./StoreTransactionsTab.module.scss";

interface IProps {
  club: IClub;
}

interface IRow {
  _id: string;
  purchased: string;
  purchased_raw: Date;
  customer: string;
  email: string;
  quantity: number;
  total: string;
  stripe_fees: string;
  eagerdog_fees: string;
  tax: string;
  club_amount: string;
  refunded: string;
  payment_type: string;
  transaction_id: string;
  transaction: ITransactionUserSummary;
  actions?: any;
}

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  [Constants.payment_method_type.card]: "Credit Card",
  [Constants.payment_method_type.cash]: "Cash",
  [Constants.payment_method_type.cheque]: "Cheque",
  [Constants.payment_method_type.etransfer]: "E-Transfer",
  [Constants.payment_method_type.other]: "Other",
};

const StoreTransactionsTab: React.FC<IProps> = ({ club }) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [rowData, setRowData] = useState<IRow[]>([]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTransactions = useCallback(async () => {
    try {
      // Get all store transactions for this club (only completed transactions)
      const transactionsResponse = await httpService.post(
        `${Config.web.REACT_APP_API_URL}/api/clubs/${club._id}/transactions/actions/search`,
        {
          query: {
            $and: [
              {
                attribute_name: "registration_type",
                attribute_value: {
                  operator: "$eq",
                  value: Constants.registration_type.store
                }
              },
              {
                attribute_name: "status",
                attribute_value: {
                  operator: "$eq",
                  value: Constants.transaction_status.success
                }
              }
            ]
          }
        }
      );

      const transactions: ITransactionUserSummary[] = transactionsResponse.data;

      // Get all add-ons for this club to populate missing titles
      const addOnsResponse = await httpService.get(
        `${Config.web.REACT_APP_API_URL}/api/clubs/${club._id}/store/items`
      );
      const addOnsMap = new Map(addOnsResponse.data.map((addOn: any) => [addOn._id, addOn]));

      const rows: IRow[] = transactions.map((transaction: ITransactionUserSummary) => {
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: transaction.amounts.currency.toUpperCase(),
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });

        // Enrich add_ons with titles if missing
        if (transaction.add_ons) {
          transaction.add_ons = transaction.add_ons.map((addOn: any) => {
            if (!addOn.title && addOnsMap.has(addOn.add_on_id)) {
              const addOnDetails: any = addOnsMap.get(addOn.add_on_id);
              return {
                ...addOn,
                title: addOnDetails.title,
                description: addOnDetails.description,
              };
            }
            return addOn;
          });
        }

        const totalQuantity = transaction.add_ons?.reduce((sum: number, addOn: any) => sum + (addOn.quantity || 0), 0) || 0;

        // Calculate Stripe fees (processing fee only, not including tax)
        const stripeFees = (transaction.amounts.processed_processing_fee || 0) / 100;
        
        // Calculate Eager Dog fees (application fee only, not including tax)
        const eagerdogFees = (transaction.amounts.processed_application_fee || 0) / 100;

        // Calculate tax on fees (for Canadian clubs)
        const taxOnFees = (transaction.amounts.calculated_processing_tax || 0) / 100;

        return {
          _id: transaction._id,
          purchased: formatDate(transaction.created_at?.toString() || ""),
          purchased_raw: new Date(transaction.created_at || Date.now()),
          customer: transaction.user?.display_name || 
                   `${transaction.user_first_name || ""} ${transaction.user_last_name || ""}`.trim() || 
                   "Unknown",
          email: transaction.user?.email || transaction.user_email || "",
          quantity: totalQuantity,
          total: formatter.format(((transaction.amounts.processed_total_amount || 0) / 100) || 0),
          stripe_fees: formatter.format(stripeFees),
          eagerdog_fees: formatter.format(eagerdogFees),
          tax: formatter.format(taxOnFees),
          club_amount: formatter.format(((transaction.amounts.processed_amount || 0) / 100)),
          refunded: formatter.format((transaction.amounts.refunded_amount || 0) / 100),
          payment_type: PAYMENT_TYPE_LABELS[transaction.amounts.payment_method_type] || 
                       transaction.amounts.payment_method_type?.charAt(0).toUpperCase() + 
                       transaction.amounts.payment_method_type?.slice(1) || 
                       "Unknown",
          transaction_id: transaction._id,
          transaction,
        };
      });

      setRowData(rows);
    } catch (error) {
      console.error("Failed to load store transactions:", error);
    }
  }, [club._id]);

  const colDefs: ColDef<IRow>[] = [
    {
      field: "purchased",
      headerName: "Purchased",
      minWidth: 220,
      filter: "agDateColumnFilter",
      sort: "desc",
      comparator: (valueA: string, valueB: string, nodeA: any, nodeB: any) => {
        const dateA = nodeA.data.purchased_raw.getTime();
        const dateB = nodeB.data.purchased_raw.getTime();
        return dateA - dateB;
      },
      cellRenderer: "agGroupCellRenderer",
    },
    {
      field: "customer",
      headerName: "Customer",
      minWidth: 180,
      filter: "agTextColumnFilter",
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 260,
      filter: "agTextColumnFilter",
    },
    {
      field: "quantity",
      headerName: "Qty",
      minWidth: 80,
      maxWidth: 100,
      filter: "agNumberColumnFilter",
      flex: 0,
    },
    {
      field: "total",
      headerName: "Total",
      minWidth: 120,
      filter: "agTextColumnFilter",
    },
    {
      field: "stripe_fees",
      headerName: "Stripe Fees",
      minWidth: 150,
      filter: "agTextColumnFilter",
    },
    {
      field: "eagerdog_fees",
      headerName: "Eager Dog Fees",
      minWidth: 170,
      filter: "agTextColumnFilter",
    },
    // Only show tax column for Canadian clubs
    ...(club.address?.country === "Canada" ? [{
      field: "tax" as keyof IRow,
      headerName: "Tax",
      minWidth: 120,
      filter: "agTextColumnFilter",
    }] : []),
    {
      field: "club_amount",
      headerName: "Club Amount",
      minWidth: 170,
      filter: "agTextColumnFilter",
    },
    {
      field: "refunded",
      headerName: "Refunded",
      minWidth: 140,
      filter: "agTextColumnFilter",
    },
    {
      field: "payment_type",
      headerName: "Payment Type",
      minWidth: 170,
      filter: "agTextColumnFilter",
    },
    {
      field: "transaction_id",
      headerName: "Transaction ID",
      minWidth: 200,
      filter: "agTextColumnFilter",
    },
    {
      field: "actions",
      headerName: "",
      cellRenderer: ActionsRenderer,
      cellRendererParams: {
        clubId: club._id,
        getTransactions: getTransactions,
      },
      minWidth: 60,
      maxWidth: 60,
      flex: 0,
      sortable: false,
      filter: false,
    },
  ];

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      getTransactions();
    }
  }, [loaded, getTransactions]);

  return (
    <div className={styles.StoreTransactionsTab}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1>Store Transactions</h1>
          <p className={styles.subtitle}>View all store purchases and orders</p>
        </div>
        <div className={styles.headerActions}>
          <Button
            onClick={() => {
              download.pushDownload({
                type: "club_store_transactions",
                clubId: club._id,
              });
            }}
          >
            Export
          </Button>
        </div>
      </header>
      <div className={styles.tableWrap}>
        <div className={styles.tableWrapInner}>
          <AgGridReact
            rowData={rowData}
            columnDefs={colDefs}
            domLayout="normal"
            pagination={true}
            paginationPageSize={25}
            paginationPageSizeSelector={[10, 25, 50, 100]}
            masterDetail={true}
            detailCellRenderer={TransactionDetailRenderer}
            detailRowAutoHeight={true}
            embedFullWidthRows={true}
            gridOptions={{
              defaultColDef: {
                flex: 1,
                minWidth: 100,
                resizable: true,
                sortable: true,
              },
              animateRows: true,
              rowHeight: 48,
              headerHeight: 48,
              overlayNoRowsTemplate: '<span>No store transactions yet</span>',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default StoreTransactionsTab;

